import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { webUrl } from '@/lib/api'
import { StatCard, Table, Pagination, Alert, Select, formatDate, formatNumber, type TableColumn } from '@/ui'
import { CalendarClock, CalendarHeart, CalendarCheck, CalendarDays } from 'lucide-react'
import { toYmd } from '@/lib/dateRange'
import styles from './Invitations.module.css'

interface UpcomingRow {
  id: string
  slug: string | null
  groomName: string | null
  brideName: string | null
  weddingDate: string
  dDay: number
  user: { id: string; email: string; name: string | null }
}

interface UpcomingResponse {
  from: string
  to: string | null
  buckets: { d7: number; d30: number; d90: number; total: number }
  invitations: UpcomingRow[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

// 미래 지향 기간 프리셋: from=오늘(서버 기본값) 고정, to=오늘+N (전체는 to 생략)
const WITHIN_OPTIONS = [
  { value: '', label: '전체 예정' },
  { value: '7', label: 'D-7 이내' },
  { value: '30', label: 'D-30 이내' },
  { value: '90', label: 'D-90 이내' },
]

export function UpcomingWeddingsTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const within = searchParams.get('within') ?? ''
  const page = Number(searchParams.get('uwPage') ?? '1') || 1

  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('pageSize', '20')
  if (within) {
    // from 은 서버가 오늘(KST)로 기본 처리 → to 상한만 전달
    const to = new Date()
    to.setDate(to.getDate() + Number(within))
    params.set('to', toYmd(to))
  }

  const { data, isLoading, error } = useApiFetch<UpcomingResponse>(
    `/api/admin/dashboard/upcoming-weddings?${params.toString()}`,
  )

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key === 'within') next.delete('uwPage') // 필터 변경 시 페이지 리셋
    setSearchParams(next, { replace: true })
  }

  const columns: TableColumn<UpcomingRow>[] = [
    {
      key: 'couple',
      label: '신랑·신부',
      width: '200px',
      render: (row) => (
        <div className={styles.couple}>
          <span>
            {row.groomName ?? '-'}
            <span className={styles.coupleHeart}>♥</span>
            {row.brideName ?? '-'}
          </span>
          <span className={styles.coupleSub}>{row.id.slice(0, 12)}…</span>
        </div>
      ),
    },
    {
      key: 'weddingDate',
      label: '예식일',
      width: '150px',
      render: (row) => formatDate(row.weddingDate, 'yyyy.M.d (EEE)'),
    },
    {
      key: 'dday',
      label: 'D-day',
      width: '90px',
      render: (row) => (row.dDay === 0 ? '오늘' : `D-${row.dDay}`),
    },
    {
      key: 'user',
      label: '유저',
      width: '220px',
      render: (row) => (
        <div className={styles.userCell}>
          <span>{row.user.name ?? row.user.email}</span>
          <span className={styles.userId}>{row.user.id}</span>
        </div>
      ),
    },
    {
      key: 'view',
      label: '',
      width: '60px',
      render: (row) => {
        const path = webUrl(row.slug ? `/letter/${row.slug}` : `/inv/${row.id}`)
        return (
          <a href={path} target="_blank" rel="noreferrer" className={styles.viewLink}>
            보기 ↗
          </a>
        )
      },
    },
  ]

  return (
    <>
      <div className={styles.filters}>
        <Select
          size="sm"
          value={within}
          onChange={(e) => setParam('within', e.target.value)}
          options={WITHIN_OPTIONS}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className={styles.statsRow}>
        <StatCard label="D-7 이내" value={formatNumber(data?.buckets.d7 ?? 0)} icon={<CalendarClock size={20} />} />
        <StatCard label="D-30 이내" value={formatNumber(data?.buckets.d30 ?? 0)} icon={<CalendarHeart size={20} />} />
        <StatCard label="D-90 이내" value={formatNumber(data?.buckets.d90 ?? 0)} icon={<CalendarCheck size={20} />} />
        <StatCard label="전체 예정" value={formatNumber(data?.buckets.total ?? 0)} icon={<CalendarDays size={20} />} />
      </div>

      <Table<UpcomingRow>
        columns={columns}
        data={data?.invitations ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(p) => setParam('uwPage', String(p))}
          size="sm"
        />
      )}
    </>
  )
}
